import * as React from 'react';
import { Check, ChevronDown, X } from 'lucide-react';

import { cn } from '@/lib/utils';

export interface OpcaoCombobox {
  valor: string;
  rotulo: string;
  /** Linha secundária: endereço do local, conselho do profissional, etc. */
  detalhe?: string | null;
}

interface ComboboxProps {
  id?: string;
  opcoes: OpcaoCombobox[];
  valor: string | null;
  aoEscolher: (valor: string | null) => void;
  placeholder?: string;
  /** Texto quando a busca não casa com nada. */
  semResultado?: string;
  carregando?: boolean;
  disabled?: boolean;
  className?: string;
  'aria-invalid'?: boolean;
  'aria-describedby'?: string;
  'aria-required'?: boolean;
}

/**
 * Teto de opções desenhadas de uma vez.
 *
 * O cadastro deixou de ser pequeno: a importação da Tabela Unificada traz
 * quase 2 mil procedimentos, e o quadro de um hospital passa de mil
 * profissionais. Pintar tudo a cada abertura da lista trava a janela — e uma
 * lista que ninguém rola até o fim não perde nada com o corte, desde que diga
 * que ele existe.
 */
const MAXIMO_DESENHADO = 100;

/** Remove acento e caixa para que "policlinica" encontre "Policlínica". */
function normalizar(texto: string): string {
  return texto
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
}

/**
 * Menu de escolha única com busca por digitação.
 *
 * Existe porque as listas do agendamento manual — locais, procedimentos,
 * profissionais — crescem até a casa das centenas, e um `<select>` nativo com
 * esse tamanho obriga o operador a rolar procurando com os olhos. Aqui ele
 * digita parte do nome e a lista se fecha em torno do que interessa.
 *
 * A escolha continua restrita ao cadastro: o texto digitado filtra, nunca vira
 * valor. É o que mantém o nome que chega ao paciente igual ao que a
 * administração cadastrou.
 */
export function Combobox({
  id,
  opcoes,
  valor,
  aoEscolher,
  placeholder = 'Digite para buscar',
  semResultado = 'Nada encontrado.',
  carregando,
  disabled,
  className,
  ...aria
}: ComboboxProps) {
  const [aberto, setAberto] = React.useState(false);
  const [busca, setBusca] = React.useState('');
  const [emFoco, setEmFoco] = React.useState(0);

  const container = React.useRef<HTMLDivElement>(null);
  const entrada = React.useRef<HTMLInputElement>(null);
  const lista = React.useRef<HTMLUListElement>(null);

  const escolhida = opcoes.find((opcao) => opcao.valor === valor) ?? null;

  const filtradas = React.useMemo(() => {
    const termo = normalizar(busca.trim());
    if (!termo) return opcoes;
    return opcoes.filter(
      (opcao) =>
        normalizar(opcao.rotulo).includes(termo) ||
        (opcao.detalhe ? normalizar(opcao.detalhe).includes(termo) : false),
    );
  }, [opcoes, busca]);

  // A navegação e a escolha valem sobre o que está à vista: ninguém pode
  // selecionar com o teclado uma opção que não está desenhada.
  const visiveis = React.useMemo(() => filtradas.slice(0, MAXIMO_DESENHADO), [filtradas]);
  const ocultas = filtradas.length - visiveis.length;

  // Fecha ao clicar fora. `pointerdown` e não `click`: o clique numa opção
  // já é tratado no próprio item, e esperar o `click` fecharia antes.
  React.useEffect(() => {
    if (!aberto) return;

    const aoApontarFora = (evento: PointerEvent) => {
      if (!container.current?.contains(evento.target as Node)) {
        setAberto(false);
        setBusca('');
      }
    };

    document.addEventListener('pointerdown', aoApontarFora);
    return () => document.removeEventListener('pointerdown', aoApontarFora);
  }, [aberto]);

  // Mantém a opção em foco visível durante a navegação pelo teclado.
  React.useEffect(() => {
    if (!aberto) return;
    lista.current?.children[emFoco]?.scrollIntoView({ block: 'nearest' });
  }, [aberto, emFoco]);

  const abrir = () => {
    if (disabled) return;
    setAberto(true);
    setEmFoco(
      Math.max(
        0,
        visiveis.findIndex((opcao) => opcao.valor === valor),
      ),
    );
  };

  const escolher = (opcao: OpcaoCombobox) => {
    aoEscolher(opcao.valor);
    setBusca('');
    setAberto(false);
    entrada.current?.focus();
  };

  const limpar = () => {
    aoEscolher(null);
    setBusca('');
    entrada.current?.focus();
  };

  const aoTeclar = (evento: React.KeyboardEvent<HTMLInputElement>) => {
    if (evento.key === 'ArrowDown' || evento.key === 'ArrowUp') {
      evento.preventDefault();
      if (!aberto) {
        abrir();
        return;
      }
      const passo = evento.key === 'ArrowDown' ? 1 : -1;
      setEmFoco((atual) => {
        if (visiveis.length === 0) return 0;
        return (atual + passo + visiveis.length) % visiveis.length;
      });
      return;
    }

    if (evento.key === 'Enter' && aberto) {
      evento.preventDefault();
      const opcao = visiveis[emFoco];
      if (opcao) escolher(opcao);
      return;
    }

    if (evento.key === 'Escape' && aberto) {
      evento.preventDefault();
      setAberto(false);
      setBusca('');
      return;
    }

    // Tab sai do campo: fechar aqui evita a lista pairando sobre o próximo.
    if (evento.key === 'Tab') {
      setAberto(false);
      setBusca('');
    }
  };

  const idLista = `${id ?? 'combobox'}-lista`;

  return (
    <div ref={container} className={cn('relative', className)}>
      <div className="relative">
        <input
          ref={entrada}
          id={id}
          type="text"
          role="combobox"
          autoComplete="off"
          disabled={disabled}
          aria-expanded={aberto}
          aria-controls={idLista}
          aria-autocomplete="list"
          aria-activedescendant={aberto && visiveis[emFoco] ? `${idLista}-${emFoco}` : undefined}
          value={aberto ? busca : (escolhida?.rotulo ?? '')}
          placeholder={carregando ? 'Carregando…' : (escolhida?.rotulo ?? placeholder)}
          onChange={(evento) => {
            setBusca(evento.target.value);
            setEmFoco(0);
            setAberto(true);
          }}
          onFocus={abrir}
          onKeyDown={aoTeclar}
          className={cn(
            'border-entrada bg-cartao text-cartao-texto h-10 w-full rounded-md border py-2 pr-16 pl-3 text-sm',
            'placeholder:text-silenciado-texto',
            'focus-visible:outline-foco focus-visible:outline-2 focus-visible:outline-offset-2',
            'disabled:cursor-not-allowed disabled:opacity-50',
            'aria-[invalid=true]:border-destrutivo aria-[invalid=true]:outline-destrutivo',
          )}
          {...aria}
        />

        <div className="absolute inset-y-0 right-2 flex items-center gap-0.5">
          {escolhida && !disabled && (
            <button
              type="button"
              onClick={limpar}
              aria-label={`Limpar seleção (${escolhida.rotulo})`}
              className="text-silenciado-texto hover:text-cartao-texto focus-visible:outline-foco rounded p-1 focus-visible:outline-2"
            >
              <X className="size-3.5" aria-hidden="true" />
            </button>
          )}
          <ChevronDown className="text-silenciado-texto size-4" aria-hidden="true" />
        </div>
      </div>

      {aberto && (
        <ul
          ref={lista}
          id={idLista}
          role="listbox"
          className="border-borda bg-cartao absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-md border py-1 shadow-lg"
        >
          {visiveis.length === 0 && (
            <li className="text-silenciado-texto px-3 py-2 text-sm">
              {carregando ? 'Carregando…' : semResultado}
            </li>
          )}

          {visiveis.map((opcao, indice) => {
            const selecionada = opcao.valor === valor;
            return (
              <li
                key={opcao.valor}
                id={`${idLista}-${indice}`}
                role="option"
                aria-selected={selecionada}
                onPointerDown={(evento) => {
                  // Impede o blur antes do clique registrar a escolha.
                  evento.preventDefault();
                  escolher(opcao);
                }}
                onPointerEnter={() => setEmFoco(indice)}
                className={cn(
                  'flex cursor-pointer items-start gap-2 px-3 py-2 text-sm',
                  indice === emFoco && 'bg-silenciado',
                )}
              >
                <Check
                  className={cn('mt-0.5 size-3.5 shrink-0', !selecionada && 'invisible')}
                  aria-hidden="true"
                />
                <span className="min-w-0">
                  <span className="block truncate">{opcao.rotulo}</span>
                  {opcao.detalhe && (
                    <span className="text-silenciado-texto block truncate text-xs">
                      {opcao.detalhe}
                    </span>
                  )}
                </span>
              </li>
            );
          })}

          {ocultas > 0 && (
            <li className="text-silenciado-texto bg-silenciado px-3 py-2 text-xs">
              +{ocultas} não exibidas. Digite mais para estreitar a busca.
            </li>
          )}
        </ul>
      )}
    </div>
  );
}
